import { Button, Drawer, Form, Input, InputRef, Space, Tooltip } from "antd";
import { useContext, useEffect, useState, useRef } from "react";
import { IDrawerModel, IOrganisationDetails } from "../../../Types";
import { useClient } from "../../Context/ClientContext";
import { notificationContext } from "../../Common/Routing/PageRoute";
import OrganisationsService, {
  IOrganiseData,
} from "../../Services/Organisations/OrganisationsService";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { CloseOutlined } from "@ant-design/icons";
import { useHotkey } from "../../Context/HotKeyContext";
import EntryLevelKeys from "../../Common/EntryLevelKeys";

const AddOrganisationModel = ({
  isModalOpen,
  setOpenModel,
  id,
  setTrigger,
  setNewResponse,
  placement,
}: IOrganisationDetails) => {
  const { showKeys } = useHotkey();
  const autoCompleteRef = useRef<InputRef>(null);
  const [form] = Form.useForm<IOrganiseData>();
  const { currentClient } = useClient();
  const { openNotification } = useContext(notificationContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [duplicatedData, setDuplicateData] = useState<boolean>(false);
  const [organisationData, setOrganisationData] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (autoCompleteRef.current) {
        autoCompleteRef.current.focus();
      }
    }, 100); // Adjust delay if needed

    return () => clearTimeout(timer);
  }, []); // Ensures autofocus after initial render

  const saveData = async (formData: IOrganiseData) => {
    setLoading(true);
    await OrganisationsService.postOrganisation(
      currentClient?.id!,
      formData,
      id!
    )
      .then((val: IApiResponse<IIdNameModel>) => {
        if (val.result) {
          openNotification(
            "success",
            `${val?.result?.name} organisation ${
              id ? "updated" : "added"
            } successfully`
          );
          setOpenModel({
            type: "",
            param: {},
          });
          setLoading(false);
          setTrigger && setTrigger((prev) => !prev);
          setNewResponse && setNewResponse(val.result);
        } else {
          setLoading(false);
          openNotification("error", val.message);
        }
      })
      .catch((er) => {
        openNotification("error", er.message);
        setLoading(false);
      });
  };

  const submitForm = (val: IOrganiseData) => {
    saveData(val);
  };

  useEffect(() => {
    (async () => {
      await OrganisationsService.getOrgOptionsList(currentClient?.id!)
        .then((val) => {
          if (val) {
            setOrganisationData(val!.map((each: IIdNameModel) => each.name));
          }
          setLoading(false);
        })
        .catch((er) => {
          console.log("Data fetching error: ", er.message);
          setLoading(false);
        });
    })();
  }, []);

  const handleDuplicates = (orgName: string) => {
    let filteredData = organisationData.filter(
      (e) => e.toLowerCase().trim() === orgName.toLowerCase().trim()
    );
    if (filteredData.length > 0) setDuplicateData(true);
    else setDuplicateData(false);
  };

  const getData = async () => {
    setLoading(true);
    await OrganisationsService.getOrganisation(currentClient?.id!, id!)
      .then((val: IOrganiseData | null) => {
        form.setFieldValue("orgName", val?.orgName);
        setLoading(false);
      })
      .catch((er) => {
        openNotification("error", er.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (id) getData();
  }, [id]);

  EntryLevelKeys({
    save: () => {
      form.validateFields().then((val) => {
        let newVal = { ...val, orgName: val.orgName.trim() };
        submitForm(newVal);
      });
    },
    cancel: () =>
      setOpenModel({
        type: "",
        param: {},
      }),
  });

  return (
    <Drawer
      open={isModalOpen}
      onClose={() =>
        setOpenModel({
          type: "",
          param: {},
        })
      }
      placement={placement ?? "right"}
      title={`${id ? "Update" : "Add"} organisation`}
      width={"500px"}
      maskClosable={false}
      closable={false}
      extra={
        <Space>
          <CloseOutlined
            onClick={() =>
              setOpenModel({
                type: "",
                param: {},
              })
            }
          />
        </Space>
      }
      footer={
        <div style={{ textAlign: "right" }}>
          <Space>
            <Tooltip
              title={<p>ALT+C</p>}
              placement="bottomLeft"
              open={showKeys}
            >
              <Button
                disabled={loading}
                onClick={() => {
                  form.resetFields();
                  setOpenModel({
                    type: "",
                    param: {},
                  });
                }}
              >
                Cancel
              </Button>
            </Tooltip>

            <Tooltip
              title={<p>ALT+S</p>}
              placement="bottomLeft"
              open={showKeys}
            >
              <Button
                disabled={loading}
                loading={loading}
                type="primary"
                onClick={() => {
                  form.validateFields().then((val) => {
                    let newVal = { ...val, orgName: val.orgName.trim() };
                    submitForm(newVal);
                  });
                }}
              >
                {`${id ? "Update" : "Save"}`}
              </Button>
            </Tooltip>
          </Space>
        </div>
      }
    >
      <Form
        name="organisation"
        form={form}
        disabled={loading}
        layout="vertical"
      >
        <Form.Item
          name="orgName"
          label="Organisation's Name"
          rules={[
            { required: true },
            {
              pattern:
                /^(?!.* {2})([A-Za-z][A-Za-z0-9_()&\-., ]{1,48}[A-Za-z0-9])$/,
              message: "Value's length must be between 3 and 50",
            },
            {
              validator: () => {
                if (duplicatedData)
                  return Promise.reject("Organisation Name already exist");
                else return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            type="text"
            maxLength={50}
            minLength={3}
            ref={autoCompleteRef}
            placeholder="Enter name here..."
            onChange={(e) => handleDuplicates(e.target.value)}
          ></Input>
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddOrganisationModel;
